import { ClickAwayListener, FormControl, InputLabel, OutlinedInput, Paper, Popper, useTheme } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks'
import type { TreeViewBaseItem } from '@mui/x-tree-view/models'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Organization } from 'types/organization'
import type { UserProfile } from 'types/user-profile'
import { getUniqueOrganizationRoles } from 'types/user-profile'
import './MultiOrganizationSelector.css'
import { ThemeMode } from 'types/config'

interface MultiOrganizationSelectorProps {
  userProfile: UserProfile
  selectedOrganizationIds: string[]
  handleChange: (selected: string[]) => void
  className?: string
  style?: React.CSSProperties
  width?: string | number
  label?: string
  placeholder?: string
  disabled?: boolean
  selectAll?: boolean
  variant?: 'standard' | 'outlined'
  labelColor?: string
  labelBackgroundColor?: string
  autoSelectDescendants?: boolean
}

const MultiOrganizationSelector: FC<MultiOrganizationSelectorProps> = ({
  userProfile,
  selectedOrganizationIds,
  handleChange,
  className,
  style,
  width = '100%',
  label = 'Select Teams',
  placeholder = 'Select Teams',
  disabled = false,
  selectAll = false,
  variant = 'outlined',
  labelColor = undefined,
  labelBackgroundColor = undefined,
  autoSelectDescendants = true,
}) => {
  const theme = useTheme()
  const apiRef = useTreeViewApiRef()
  const toggledItemRef = useRef<{ [itemId: string]: boolean }>({})
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [inputWidth, setInputWidth] = useState(0)

  const uniqueUserRoles = useMemo(() => getUniqueOrganizationRoles(userProfile), [userProfile])

  // Organization map for efficient lookups
  const organizationMap = useMemo(() => {
    const map = new Map<string, Organization>()
    uniqueUserRoles.forEach((role) => {
      map.set(role.organization.id, role.organization)
    })
    return map
  }, [uniqueUserRoles])

  // Selected organization names for display
  const selectedOrganizationNames = useMemo(() => {
    return selectedOrganizationIds.map((id) => organizationMap.get(id)?.name || id)
  }, [selectedOrganizationIds, organizationMap])

  useEffect(() => {
    if (selectAll) {
      handleChange(uniqueUserRoles.map((role) => role.organization.id))
    }
  }, [selectAll, uniqueUserRoles])

  const handleItemSelectionToggle = useCallback((event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
    toggledItemRef.current[itemId] = isSelected
  }, [])

  const getItemDescendantsIds = useCallback((item: TreeViewBaseItem): string[] => {
    const ids: string[] = []
    item.children?.forEach((child) => {
      ids.push(child.id)
      ids.push(...getItemDescendantsIds(child))
    })
    return ids
  }, [])

  const handleSelectedItemsChange = useCallback(
    (event: React.SyntheticEvent, newSelectedItems: string[]) => {
      if (!autoSelectDescendants) {
        handleChange(newSelectedItems)
        toggledItemRef.current = {}
        return
      }

      const itemsToSelect: string[] = []
      const itemsToUnSelect: { [itemId: string]: boolean } = {}

      Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
        const item = apiRef.current?.getItem(itemId)
        if (item) {
          if (isSelected) {
            itemsToSelect.push(...getItemDescendantsIds(item))
          } else {
            getItemDescendantsIds(item).forEach((descendantId) => {
              itemsToUnSelect[descendantId] = true
            })
          }
        }
      })

      const newSelectedItemsWithChildren = Array.from(
        new Set([...newSelectedItems, ...itemsToSelect].filter((itemId) => !itemsToUnSelect[itemId]))
      )

      handleChange(newSelectedItemsWithChildren)
      toggledItemRef.current = {}
    },
    [autoSelectDescendants, handleChange, getItemDescendantsIds]
  )

  const treeData = useMemo((): TreeViewBaseItem[] => {
    const isTopLevelOrg = (org: Organization) => !org.parentId || !organizationMap.has(org.parentId)

    const buildTree = (org: Organization): TreeViewBaseItem => ({
      id: org.id,
      label: org.name,
      children: uniqueUserRoles
        .filter((role) => role.organization.parentId === org.id)
        .map((role) => buildTree(role.organization))
        .sort((a, b) => a.label.localeCompare(b.label)),
    })

    const rootOrganizations = uniqueUserRoles.map((role) => role.organization).filter(isTopLevelOrg)

    return rootOrganizations.map(buildTree).sort((a, b) => a.label.localeCompare(b.label))
  }, [uniqueUserRoles, organizationMap])

  const firstRootItemId = useMemo(() => {
    return treeData[0]?.id
  }, [treeData])

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }, [disabled])

  const handleClickAway = useCallback(() => {
    setOpen(false)
  }, [])

  // Effect to handle resize observer for input width changes
  useEffect(() => {
    const inputElement = inputRef.current
    if (!inputElement) return

    const updateWidth = () => {
      setInputWidth(inputElement.offsetWidth)
    }

    // Initial measurement
    updateWidth()

    // Create resize observer to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      updateWidth()
    })

    resizeObserver.observe(inputElement)

    // Also listen for window resize as a fallback
    const handleWindowResize = () => {
      setTimeout(updateWidth, 100) // Small delay to ensure layout has updated
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  // Effect to update width when input becomes available
  useEffect(() => {
    if (inputRef.current && inputWidth === 0) {
      setInputWidth(inputRef.current.offsetWidth)
    }
  }, [inputWidth])

  // Helper function to measure text width
  const measureTextWidth = useCallback((text: string, font: string): number => {
    if (!text) return 0
    try {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context) {
        context.font = font
        return context.measureText(text).width
      }
    } catch (error) {
      console.warn('Error measuring text width:', error)
    }
    // Fallback: rough character width estimation
    return text.length * 8
  }, [])

  // Helper function to get computed font style for input element
  const getInputFont = useCallback((): string => {
    if (inputRef.current) {
      try {
        const computedStyle = window.getComputedStyle(inputRef.current)
        const fontSize = computedStyle.fontSize || '14px'
        const fontFamily = computedStyle.fontFamily || 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif'
        return `${fontSize} ${fontFamily}`
      } catch (error) {
        console.warn('Error getting input font style:', error)
      }
    }
    return '14px Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif' // fallback
  }, [])

  // Custom truncation function that always shows count
  const getTruncatedText = useCallback(
    (names: string[], count: number, availableWidth: number): string => {
      if (names.length === 0) return placeholder
      if (names.length === 1) return names[0]
      if (availableWidth <= 0) return `...${count})`

      const font = getInputFont()
      const countText = ` (${count})`
      const countWidth = measureTextWidth(countText, font)
      const ellipsisWidth = measureTextWidth('...', font)

      // Reserve space for count and some padding
      const availableForNames = Math.max(0, availableWidth - countWidth - 20) // 20px padding buffer

      if (availableForNames <= ellipsisWidth) {
        return `...${countText}`
      }

      const joinedNames = names.join(', ')
      const joinedWidth = measureTextWidth(joinedNames, font)

      // If the full text fits, return it
      if (joinedWidth <= availableForNames) {
        return `${joinedNames}${countText}`
      }

      // Need to truncate - work backwards from the end
      let truncatedNames = ''
      let currentWidth = 0

      for (let i = 0; i < names.length; i++) {
        const nameToAdd = i === 0 ? names[i] : `, ${names[i]}`
        const nameWidth = measureTextWidth(nameToAdd, font)

        // Check if adding this name plus ellipsis would exceed available space
        if (currentWidth + nameWidth + ellipsisWidth > availableForNames) {
          // If this is the first name and it's still too long, truncate the name itself
          if (i === 0 && names[0]) {
            let truncatedName = names[0]
            while (truncatedName.length > 1) {
              truncatedName = truncatedName.slice(0, -1)
              const testWidth = measureTextWidth(truncatedName + '...', font)
              if (testWidth <= availableForNames) {
                return `${truncatedName}...${countText}`
              }
            }
            // Even single character doesn't fit, return minimal version
            return `${truncatedName[0] || ''}...${countText}`
          }
          break
        }

        truncatedNames += nameToAdd
        currentWidth += nameWidth
      }

      return `${truncatedNames}...${countText}`
    },
    [placeholder, getInputFont, measureTextWidth]
  )

  const getDisplayText = useCallback(() => {
    if (selectedOrganizationNames.length === 0) {
      return placeholder
    }

    if (selectedOrganizationNames.length === 1) {
      return selectedOrganizationNames[0]
    }

    // Get available width from the input element
    if (inputWidth === 0) {
      // Fallback to original behavior if width not measured yet
      return `${selectedOrganizationNames.join(', ')} (${selectedOrganizationNames.length} selected)`
    }

    const paddingLeft = 16 // From sx padding: '10.5px 16px'
    const paddingRight = 16
    const availableWidth = inputWidth - paddingLeft - paddingRight

    return getTruncatedText(selectedOrganizationNames, selectedOrganizationNames.length, availableWidth)
  }, [selectedOrganizationNames, placeholder, getTruncatedText, inputWidth])

  const hoverColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.action.hover : theme.palette.action.hover

  return (
    <div className={className} style={{ ...style, width }}>
      <FormControl variant={variant} sx={{ width }}>
        <InputLabel
          shrink
          variant={variant}
          sx={{
            '&.MuiInputLabel-shrink': {
              background: labelBackgroundColor || 'inherit',
              color: labelColor || undefined,
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          }}
        >
          {label}
        </InputLabel>

        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <OutlinedInput
              ref={anchorRef}
              readOnly
              value={getDisplayText()}
              onClick={handleInputClick}
              disabled={disabled}
              label={label}
              inputRef={inputRef}
              sx={{
                cursor: disabled ? 'default' : 'pointer',
                borderRadius: '21px',
                width,
                '& .MuiOutlinedInput-input': {
                  padding: '10.5px 16px',
                  cursor: disabled ? 'default' : 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color:
                    selectedOrganizationNames.length > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
                },
                '& .MuiOutlinedInput-root': {
                  width,
                },
              }}
              inputProps={{
                'aria-expanded': open,
                'aria-haspopup': 'listbox',
                'aria-label': label,
                title: selectedOrganizationNames.join(', '),
              }}
            />

            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal }}
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                },
              ]}
            >
              <Paper
                sx={{
                  width: anchorRef.current?.offsetWidth || 'auto',
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 1,
                  boxShadow: theme.shadows[8],
                }}
              >
                <RichTreeView
                  multiSelect
                  checkboxSelection
                  apiRef={apiRef}
                  items={treeData}
                  selectedItems={selectedOrganizationIds}
                  onSelectedItemsChange={handleSelectedItemsChange}
                  onItemSelectionToggle={handleItemSelectionToggle}
                  defaultExpandedItems={firstRootItemId ? [firstRootItemId] : []}
                  sx={{
                    '& .MuiTreeItem-content:hover': {
                      backgroundColor: hoverColor,
                    },
                    '& .MuiTreeItem-content.Mui-selected:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                />
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
      </FormControl>
    </div>
  )
}

export default MultiOrganizationSelector
